import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatError, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, PaperPlaneTilt, Check, X } from "@phosphor-icons/react";

export default function Conversation() {
  const { userId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  async function load() {
    try {
      const { data } = await api.get(`/me/conversations/${userId}/messages`);
      setData(data);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      toast.error("Conversa não encontrada");
      navigate("/mensagens");
    } finally { setLoading(false); }
  }

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    load();
    // eslint-disable-next-line
  }, [userId, user, authLoading]);

  async function send(e) {
    e.preventDefault();
    if (!content.trim()) return;
    setSending(true);
    try {
      await api.post(`/me/conversations/${userId}/messages`, { content });
      setContent("");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  }

  async function acceptRequest() {
    try {
      await api.post(`/me/conversations/${userId}/accept`);
      toast.success("Pedido aceite");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }
  async function declineRequest() {
    if (!confirm("Recusar pedido? A conversa será apagada.")) return;
    try {
      await api.post(`/me/conversations/${userId}/decline`);
      toast.success("Pedido recusado");
      navigate("/mensagens");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  if (loading || !data) return <div className="pt-32 text-center text-zinc-500 font-mono-accent text-xs">A CARREGAR...</div>;

  const conv = data.conversation || {};
  const other = data.other_user;
  const isRequest = conv.is_request && !conv.accepted;
  const iAmRequester = isRequest && conv.request_from === user.user_id;
  const iAmRecipient = isRequest && conv.request_from !== user.user_id;

  return (
    <div className="pt-20 max-w-3xl mx-auto px-5 lg:px-8 pb-24" data-testid="conversation-page">
      <Link to="/mensagens" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-4">
        <ArrowLeft size={16} weight="bold" /> Voltar
      </Link>

      {/* Header */}
      <Link to={`/membros/${other.slug || other.user_id}`} className="flex items-center gap-3 bg-[#121217] border border-white/5 rounded-2xl p-4 hover:border-white/15 transition-colors">
        {other.picture ? (
          <img src={resolveImageUrl(other.picture)} alt="" className="w-12 h-12 rounded-full object-cover" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] grid place-items-center font-bold">{(other.name || "?")[0]?.toUpperCase()}</div>
        )}
        <div className="flex-1">
          <div className="font-display font-bold text-lg">{other.name || "Sem nome"}</div>
          <div className="font-mono-accent text-[10px] text-zinc-500">VER PERFIL →</div>
        </div>
      </Link>

      {/* Request banner */}
      {iAmRequester && (
        <div className="mt-4 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-xl p-4 text-sm" data-testid="request-pending-banner">
          <div className="font-display font-bold text-[#F59E0B] mb-1">Pedido de mensagem pendente</div>
          <div className="text-zinc-400">Só podes enviar uma mensagem até que {other.name} aceite o teu pedido ou te siga.</div>
        </div>
      )}
      {iAmRecipient && (
        <div className="mt-4 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid="request-incoming-banner">
          <div className="flex-1 min-w-[200px]">
            <div className="font-display font-bold text-[#F59E0B] mb-1">Pedido de mensagem</div>
            <div className="text-zinc-400 text-sm">{other.name} quer falar contigo.</div>
          </div>
          <div className="flex gap-2">
            <Button onClick={acceptRequest} className="btn-crimson" data-testid="accept-request-btn"><Check size={14} weight="bold" className="mr-1" /> Aceitar</Button>
            <Button onClick={declineRequest} variant="outline" className="border-white/15" data-testid="decline-request-btn"><X size={14} weight="bold" className="mr-1" /> Recusar</Button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="mt-6 space-y-3" data-testid="messages-list">
        {data.messages.length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-xl py-10 text-center text-sm text-zinc-500">
            Sem mensagens. Envia a primeira!
          </div>
        ) : (
          data.messages.map(m => {
            const mine = m.from_user_id === user.user_id;
            return (
              <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.message_id}`}>
                <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${mine ? "bg-[#E63946] text-white rounded-br-sm" : "bg-[#121217] border border-white/5 rounded-bl-sm"}`}>
                  <div className="whitespace-pre-wrap break-words">{m.content}</div>
                  <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-zinc-500"} font-mono-accent`}>
                    {new Date(m.created_at).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      {/* Compose */}
      <form onSubmit={send} className="mt-6 sticky bottom-4 bg-[#121217] border border-white/10 rounded-2xl p-3 flex items-end gap-2">
        <Textarea
          placeholder={iAmRequester ? "Já enviaste um pedido — aguarda resposta." : "Escreve uma mensagem... (texto e emojis 😊)"}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={2}
          disabled={iAmRequester || sending}
          className="bg-[#1C1C22] border-white/10 resize-none flex-1"
          maxLength={2000}
          data-testid="message-input"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); }
          }}
        />
        <Button type="submit" disabled={sending || !content.trim() || iAmRequester} className="btn-crimson h-auto px-4 py-3" data-testid="send-message-btn">
          <PaperPlaneTilt size={18} weight="bold" />
        </Button>
      </form>
    </div>
  );
}
